import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Field, reduxForm, change } from "redux-form";
import _ from "lodash";
import Grid from "@material-ui/core/Grid/index";
import Typography from "@material-ui/core/Typography";
import { withStyles, FormHelperText } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import red from "@material-ui/core/colors/red";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";
import { Editor } from "@tinymce/tinymce-react";
import axios from "axios";
import { FAQ_CMS_REDUX_QUESTION_FORM_NAME } from "../../../../constants";
import TextToSpeech from "./TextToSpeech";
import { isString } from "../../../../common/utils";
import { uploadFaqCmsImage } from "../../../../redux/actions/faqCmsActions";
import styles from "../../styles";

const ERROR_RED_COLOR = red[500];

const getExtraFieldsForSuggestionsRequest = customFields => {
  if (!customFields) {
    return "";
  }

  const commaSeparatedCustomFields = customFields.map(field => field.sourceFieldName).join(",");
  return encodeURIComponent(commaSeparatedCustomFields);
};

const checkIfAnyFieldEmpty = (formValues, fields) => {
  const isEmptyString = val => !val || (isString(val) && !val.trim().length);
  const errors = {};
  const genericMessage = "Required";

  // Check if field value is nonexistent or only whitespace
  fields.forEach(field => {
    const value = formValues[field];

    // Subject area uses react-select
    if (field === "subjectArea") {
      if (_.isEmpty(value) || !value.value.trim().length) {
        errors[field] = genericMessage;
      }
      return;
    }

    // Question can be a simple text input or a react-select component
    if (field === "question") {
      if (_.isObject(value)) {
        const question = value.value;
        if (isEmptyString(question)) {
          errors[field] = genericMessage;
        }
        return;
      }
    }

    if (isEmptyString(value)) {
      errors[field] = genericMessage;
    }
  });

  return errors;
};

const parseFoundFaqsData = (hits, customFields) => {
  return hits.map(hit => {
    const source = hit._source;
    const parsedHit = {
      docId: hit._id,
      htmlResponse: source.answer,
      voiceResponse: source.answer_voice,
      question: source.question,
      subjectArea: source.subject_area
    };

    if (!customFields) {
      return parsedHit;
    }

    // Handle custom fields
    for (const customField of customFields) {
      const { name, sourceFieldName } = customField;
      const fieldValue = source[sourceFieldName];

      if (fieldValue) {
        parsedHit[name] = fieldValue;
      }
    }
    return parsedHit;
  });
};

const getQuestionOptionLabel = (customFieldsKeys, parsedDoc) => {
  const customFieldsValues = customFieldsKeys.map(k => parsedDoc[k]).filter(customFieldValue => customFieldValue);

  if (customFieldsValues.length === 0) {
    return parsedDoc.question;
  }

  return `${parsedDoc.question} [${customFieldsValues.join(", ")}]`;
};

class QuestionForm extends React.Component {
  static requiredFormFields = ["question", "subjectArea", "htmlResponse", "voiceResponse"];

  constructor(props) {
    super(props);
    this.state = {
      editorContent: this.props.formData.htmlResponse
    };
    this.editorConfig = {
      height: 300,
      menubar: false,
      statusbar: false,
      toolbar_mode: "wrap",
      plugins: ["code", "lists", "hr", "image", "link", "media", "searchreplace"],
      toolbar:
        "undo redo searchreplace | styleselect bold italic forecolor backcolor | hr bullist numlist | link image media | alignleft aligncenter alignright outdent indent | code",
      image_description: false,
      image_dimensions: false,
      image_uploadtab: true,
      images_upload_handler: this.props.uploadFaqCmsImage
    };
    this.questionInputName = "question";
  }

  renderHelperText = (text, style) => {
    return <FormHelperText style={{ marginLeft: "14px", marginRight: "14px", ...style }}>{text}</FormHelperText>;
  };

  getError = (touched, invalid, error) => {
    const colorRed = touched && invalid;
    const showErrorMessage = touched && error;
    const dangerStyle = { color: ERROR_RED_COLOR };

    return {
      captionStyle: colorRed ? dangerStyle : {},
      errorMessageComponent: showErrorMessage ? this.renderHelperText(error, dangerStyle) : null
    };
  };

  getSelectComponent = tagName => {
    if (tagName) {
      return tagName;
    }

    const { userIsAdministrator, nonAdminsCanCreateSubjectAreas } = this.props;
    if (userIsAdministrator || nonAdminsCanCreateSubjectAreas) {
      return CreatableSelect;
    }

    return Select;
  };

  getRestOfSelectProps = props => {
    const restOfProps = {};
    const keys = ["isClearable", "defaultValue"]; // todo default value is ever used?

    keys.forEach(k => {
      if (props.hasOwnProperty(k)) {
        restOfProps[k] = props[k];
      }
    });

    return restOfProps;
  };

  getReactSelectCustomStyles = invalid => {
    const borderColor = invalid ? { borderColor: ERROR_RED_COLOR } : {};
    return {
      option: (provided, state) => ({
        ...provided,
        fontSize: 14
      }),
      control: (provided, state) => ({
        ...provided,
        ...(state.isFocused ? {} : borderColor),
        fontSize: 14
      }),
      valueContainer: (provided, state) => ({
        ...provided,
        padding: "11px 10px"
      }),
      singleValue: (provided, state) => ({
        ...provided,
        lineHeight: "20px"
      })
    }; // Make it look more like Material UI components
  };

  renderReactSelect = props => {
    const {
      input,
      options,
      placeholder,
      disabled,
      label,
      tagName,
      meta: { touched, invalid, error }
    } = props;
    const customStyles = this.getReactSelectCustomStyles(touched && invalid);
    const { captionStyle, errorMessageComponent } = this.getError(touched, invalid, error);
    const TagName = this.getSelectComponent(tagName);
    const restOfSelectProps = this.getRestOfSelectProps(props);

    return (
      <React.Fragment>
        <Typography style={captionStyle} variant="caption">
          {`${label} *`}
        </Typography>
        <TagName
          menuPortalTarget={document.body} // This puts the element above all other elements (no need for z-index)
          {...input}
          onChange={value => input.onChange(value)}
          onBlur={() => input.onBlur(input.value)}
          options={options}
          placeholder={placeholder}
          isDisabled={disabled}
          styles={customStyles}
          {...restOfSelectProps}
        />
        {errorMessageComponent}
      </React.Fragment>
    );
  };

  // Based on renderTextField in utils.js
  renderEditor = ({ label, input, meta: { touched, invalid, error }, ...custom }) => {
    const { captionStyle, errorMessageComponent } = this.getError(touched, invalid, error);

    return (
      <React.Fragment>
        <Typography style={captionStyle} variant="caption">
          HTML Response *
        </Typography>
        <Editor
          init={this.editorConfig}
          value={input.content !== "" ? input.content : null}
          /*
           * Todo should onBlur be used instead of onEditorChange for better performance?
           * https://github.com/instructure-react/react-tinymce/issues/22#issuecomment-234448202
           */
          onEditorChange={e => input.onChange(e)}
          disabled={this.props.formDisabled}
          {...input}
          {...custom}
        />
        {errorMessageComponent}
      </React.Fragment>
    );
  };

  /**
   * The div is just used to create some space.
   */
  renderTextToSpeech = () => {
    return (
      <React.Fragment>
        <TextToSpeech />
        <div style={{ marginTop: "0.4rem" }}></div>
      </React.Fragment>
    );
  };

  renderTextFieldWithCustomCaption = ({
    label,
    placeholder,
    input,
    includeTextToSpeech,
    meta: { touched, invalid, error },
    ...custom
  }) => {
    const { captionStyle } = this.getError(touched, invalid, error);

    return (
      <React.Fragment>
        <Typography style={captionStyle} variant="caption">
          {label}
        </Typography>
        {includeTextToSpeech ? this.renderTextToSpeech() : null}
        <TextField
          placeholder={placeholder}
          error={touched && invalid}
          helperText={touched && error}
          {...input}
          {...custom}
        />
      </React.Fragment>
    );
  };

  renderCustomFields = () => {
    const { customFields, formDisabled } = this.props;

    if (!customFields) {
      return null;
    }
    return customFields.map((customFieldSelect, i) => {
      const { name, label, options } = customFieldSelect;
      if (options[0].value !== "All") options.unshift({ value: "All", label: "All" }); //dodaj all kao default
      return (
        <Grid item xs={12} key={i}>
          <Field
            name={name}
            label={label}
            component={this.renderReactSelect}
            options={options}
            placeholder={label}
            disabled={formDisabled}
            fullWidth
            tagName={Select}
            isClearable={false}
          />
        </Grid>
      );
    });
  };

  loadQuestionSuggestions = query => {
    const { customFields } = this.props;
    const encodedQuery = encodeURIComponent(query);
    const encodedExtraFields = getExtraFieldsForSuggestionsRequest(customFields);
    return axios
      .get(
        `https://api.responsum.ai/development/public-chat-bot/suggestions-v2/${this.props.esIndexName}/${encodedQuery}?dontGroupResults=true&extraFields=${encodedExtraFields}`
      )
      .then(response => {
        // Custom fields are optional
        const customFieldsInputNames = customFields ? customFields.map(customField => customField.name) : [];
        return parseFoundFaqsData(response.data.hits.hits, this.props.customFields).map(doc => ({
          ...doc,
          // We need the two props below for react-select
          value: doc.question,
          label: getQuestionOptionLabel(customFieldsInputNames, doc)
        }));
      })
      .catch(e => {
        console.error(e);
        return [];
      });
  };

  handleCustomFieldsOnSearchableQuestionFieldChange = value => {
    const { customFields, change } = this.props;

    if (!customFields) {
      return;
    }

    // Handle custom (i.e. optional) fields when an existing option was selected
    for (const customField of customFields) {
      const { name } = customField;
      if (!value[name]) {
        // Show default (i.e. first) value for custom field if doc has no custom field value
        change(name, customField.options[0]);
        continue;
      }

      const selectOption = customField.options.find(option => option.value === value[name]);

      if (!selectOption) {
        // Show default (i.e. first) value for custom field if doc has an invalid custom field value
        change(name, customField.options[0]);
        continue;
      }

      // Change custom field value to the current value
      change(name, selectOption);
    }
  };

  handleSearchableQuestionFieldChange = (value, input) => {
    const { showMarkQuestionForDeletionButton, hideMarkQuestionForDeletionButton, change } = this.props;

    if (!value) {
      // Case when whole input is cleared (clicked the "x" icon, the focus was moved to the input and del/backspace
      // was typed or some text was typed and then deleted with del/backspace).
      input.onChange(value);

      if (hideMarkQuestionForDeletionButton) {
        hideMarkQuestionForDeletionButton();
      }

      return;
    }

    if (value.__isNew__) {
      const question = value.value;
      input.onChange({ label: question, value: question, __isNew__: value.__isNew__ });

      if (hideMarkQuestionForDeletionButton) {
        hideMarkQuestionForDeletionButton();
      }

      return;
    }

    // Case when input is not empty and an existing option was selected.
    // Populate required fields.
    input.onChange(value);
    const { htmlResponse, voiceResponse, subjectArea } = value;
    change("htmlResponse", htmlResponse);
    change("voiceResponse", voiceResponse);
    change("subjectArea", { value: subjectArea, label: subjectArea });

    if (showMarkQuestionForDeletionButton) {
      showMarkQuestionForDeletionButton();
    }

    this.handleCustomFieldsOnSearchableQuestionFieldChange(value);
  };

  renderSimpleQuestionInput = allFieldsCommonProps => {
    return <Field name={this.questionInputName} label="Question *" placeholder="Question" {...allFieldsCommonProps} />;
  };

  renderSearchableQuestionInput = () => {
    const { subjectAreas, formDisabled } = this.props;

    return (
      <Field
        name={this.questionInputName}
        label="Question"
        component={this.renderSearchableQuestionField}
        options={subjectAreas}
        placeholder="Question"
        disabled={formDisabled}
        fullWidth
        isClearable={true}
      />
    );
  };

  renderSearchableQuestionField = props => {
    const {
      input,
      placeholder,
      disabled,
      label,
      meta: { touched, invalid, error }
    } = props;
    const customStyles = this.getReactSelectCustomStyles(touched && invalid);
    const { captionStyle, errorMessageComponent } = this.getError(touched, invalid, error);
    const restOfSelectProps = this.getRestOfSelectProps(props);

    return (
      <React.Fragment>
        <Typography style={captionStyle} variant="caption">
          {`${label} *`}
        </Typography>
        <AsyncCreatableSelect
          menuPortalTarget={document.body} // This puts the element above all other elements (no need for z-index)
          {...input}
          onChange={(value, { action }) => {
            // Use the action var when when you need to tell what event was fired (e.g. "clear" or "select-option"),
            // more info: https://react-select.com/props#prop-types (see ActionTypes)
            this.handleSearchableQuestionFieldChange(value, input);
          }}
          onBlur={() => input.onBlur(input.value)}
          loadOptions={this.loadQuestionSuggestions}
          placeholder={placeholder}
          isDisabled={disabled}
          styles={customStyles}
          allowCreateWhileLoading={true}
          createOptionPosition="first"
          {...restOfSelectProps}
        />
        {errorMessageComponent}
      </React.Fragment>
    );
  };

  render() {
    const { formDisabled, handleSubmit, onSubmit, buttons, subjectAreas, searchOfFaqsEnabled } = this.props;
    const allFieldsCommonProps = {
      required: true,
      fullWidth: true,
      variant: "outlined",
      disabled: formDisabled,
      component: this.renderTextFieldWithCustomCaption
    };
    const multilineFieldsCommonProps = { multiline: true, rows: 4 };

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {searchOfFaqsEnabled
              ? this.renderSearchableQuestionInput()
              : this.renderSimpleQuestionInput(allFieldsCommonProps)}
          </Grid>

          <Grid item xs={12}>
            <Field
              name="subjectArea"
              label="Subject Area"
              component={this.renderReactSelect}
              options={subjectAreas}
              placeholder="Subject Area"
              disabled={formDisabled}
              fullWidth
              isClearable={true}
            />
          </Grid>

          {this.renderCustomFields()}

          <Grid item xs={12}>
            <Field name="htmlResponse" component={this.renderEditor} />
          </Grid>

          <Grid item xs={12}>
            <Field
              name="voiceResponse"
              label="Voice Response *"
              placeholder="Voice Response"
              includeTextToSpeech={true}
              {...allFieldsCommonProps}
              {...multilineFieldsCommonProps}
            />
          </Grid>
        </Grid>
        {buttons}
      </form>
    );
  }
}

QuestionForm = reduxForm({
  form: FAQ_CMS_REDUX_QUESTION_FORM_NAME,
  enableReinitialize: true,
  // keepDirtyOnReinitialize: true, // With this param, form validation works after this.props.initialize is called
  validate: formValues => checkIfAnyFieldEmpty(formValues, QuestionForm.requiredFormFields)
})(QuestionForm);

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        uploadFaqCmsImage,
        change
      },
      dispatch
    ),
    dispatch
  };
}

const mapStateToProps = (state, ownProps) => {
  return {
    initialValues: ownProps.formData,
    userIsAdministrator: _.get(state, "auth.cognitoUser.attributes.custom:role", "user") === "Administrator" // This is available right after login
  };
};

QuestionForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionForm);

export default withStyles(styles)(QuestionForm);
